import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AccountInfo } from '@azure/msal-browser'
import { msalInstance, loginRequest } from '@/api/msalConfig'
import { authApi } from '@/api/authApi'

export const useAuthStore = defineStore('auth', () => {
    const account = ref<AccountInfo | null>(null)
    const isAdmin = ref(false)

    const isAuthenticated = computed(() => account.value !== null)
    const userName = computed(() => account.value?.name ?? '')
    const userEmail = computed(() => account.value?.username ?? '')

    async function fetchUserRole() {
        try {
            const me = await authApi.me()
            isAdmin.value = me.isAdmin
        } catch {
            isAdmin.value = false
        }
    }

    async function initialize() {
        await msalInstance.initialize()
        const result = await msalInstance.handleRedirectPromise()

        if (result?.account) {
            account.value = result.account
            msalInstance.setActiveAccount(result.account)
            await fetchUserRole()
            return
        }

        const active = msalInstance.getActiveAccount()
        if (active) {
            account.value = active
            await fetchUserRole()
            return
        }

        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
            account.value = accounts[0]
            msalInstance.setActiveAccount(accounts[0])
            await fetchUserRole()
        }
    }

    async function login() {
        await msalInstance.loginRedirect(loginRequest)
    }

    async function logout() {
        await msalInstance.logoutRedirect()
        account.value = null
        isAdmin.value = false
    }

    return { account, isAuthenticated, isAdmin, userName, userEmail, initialize, login, logout }
})
